import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import {
	MenuFoldOutlined,
	MenuUnfoldOutlined,
	MenuOutlined,
	CloseOutlined,
	RightOutlined,
	HomeOutlined,
} from "@ant-design/icons";
import routes from "../routes";
import Sidenav from "../components/layout/Sidenav";
import TheHeaderDropdown from "./TheHeaderDropdown";

const DESKTOP_BREAKPOINT = 1024;

const SadAntLayout = ({ children }) => {
	const location = useLocation();

	const [collapsed, setCollapsed] = useState(false);
	const [mobileOpen, setMobileOpen] = useState(false);
	const [isDesktop, setIsDesktop] = useState(
		typeof window === "undefined" ? true : window.innerWidth >= DESKTOP_BREAKPOINT
	);

	useEffect(() => {
		const onResize = () => setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT);
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	// navigating on a phone should close the drawer behind you
	useEffect(() => {
		setMobileOpen(false);
	}, [location.pathname]);

	// the drawer must not leave the page scrollable underneath it
	useEffect(() => {
		document.body.style.overflow = mobileOpen ? "hidden" : "";
		return () => { document.body.style.overflow = ""; };
	}, [mobileOpen]);

	/* ---------------- breadcrumb ---------------- */

	const pathSnippets = location.pathname.split("/").filter(Boolean);
	const crumbs = pathSnippets
		.map((_, index) => {
			const url = `/${pathSnippets.slice(0, index + 1).join("/")}`;
			const routeElement = routes[1]?.routes?.find((element) => element.path === url);
			return routeElement ? { url, name: routeElement.name } : null;
		})
		.filter(Boolean);

	const currentTitle = crumbs.length ? crumbs[crumbs.length - 1].name : "صفحه نخست";
	const sidebarCollapsed = isDesktop && collapsed;

	return (
		<div className={`shell ${sidebarCollapsed ? "shell--collapsed" : ""}`}>
			<Toaster
				position="top-center"
				toastOptions={{
					style: {
						borderRadius: '14px',
						background: '#fff',
						color: '#1f2937',
						boxShadow: '0 10px 30px rgba(79,70,229,0.16)',
						border: '1px solid #e0e7ff',
						padding: '12px 16px',
						fontSize: '14px',
					},
					success: { iconTheme: { primary: '#4f46e5', secondary: '#fff' } },
					error: { iconTheme: { primary: '#ef4444', secondary: '#fff' }, style: { border: '1px solid #fecaca' } },
					loading: { iconTheme: { primary: '#7c3aed', secondary: '#fff' } },
				}}
			/>

			{mobileOpen && <div className="shell-backdrop" onClick={() => setMobileOpen(false)} />}

			<aside className={`shell-sidebar ${mobileOpen ? "is-open" : ""}`}>
				<button
					className="shell-drawer-close"
					onClick={() => setMobileOpen(false)}
					title="بستن منو"
				>
					<CloseOutlined />
				</button>

				<Sidenav
					collapsed={sidebarCollapsed}
					onExpand={() => setCollapsed(false)}
					onNavigate={() => setMobileOpen(false)}
				/>
			</aside>

			<div className="shell-main">
				<header className="shell-topbar">
					<button
						className="shell-toggle shell-toggle--desktop"
						onClick={() => setCollapsed((v) => !v)}
						title={collapsed ? "باز کردن منو" : "جمع کردن منو"}
					>
						{collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
					</button>

					<button
						className="shell-toggle shell-toggle--mobile"
						onClick={() => setMobileOpen(true)}
						title="باز کردن منو"
					>
						<MenuOutlined />
					</button>

					<div className="shell-heading">
						<h1 className="shell-title">{currentTitle}</h1>
						<nav className="shell-crumbs">
							<Link to="/" className="shell-crumb">
								<HomeOutlined /> پایش فضای شناختی
							</Link>
							{crumbs.map((crumb) => (
								<React.Fragment key={crumb.url}>
									<RightOutlined className="shell-crumb-sep" />
									<Link to={crumb.url} className="shell-crumb">
										{crumb.name}
									</Link>
								</React.Fragment>
							))}
						</nav>
					</div>

					{/* account: always one tap away, on every screen size */}
					<div className="shell-topbar-actions">
						<TheHeaderDropdown />
					</div>
				</header>

				<main className="shell-content">{children}</main>

				<footer className="shell-footer">
					<span className="shell-footer-line" />
					<span className="shell-footer-text">
						پایش فضای شناختی — نسخه {window.rasekhVersion}
					</span>
					<span className="shell-footer-line" />
				</footer>
			</div>

			<style>{`
                .shell {
                    --sidebar-w: 288px;
                    --sidebar-w-collapsed: 84px;
                    --topbar-h: 68px;
                    direction: rtl;
                    min-height: 100vh;
                    background: #f5f6fb;
                    font-family: inherit;
                }
                .shell--collapsed { --sidebar-w: var(--sidebar-w-collapsed); }

                /* ---------- sidebar ---------- */
                .shell-sidebar {
                    position: fixed; top: 0; right: 0; bottom: 0;
                    width: var(--sidebar-w);
                    display: flex; flex-direction: column;
                    background: #1e1b4b;
                    box-shadow: -4px 0 24px rgba(30,27,75,.16);
                    z-index: 40;
                    transition: width .24s ease, transform .26s ease;
                }
                /* the menu now owns the whole column */
                .shell-sidebar > nav { flex: 1; min-height: 0; }
                .shell-drawer-close { display: none; }

                /* ---------- main ---------- */
                .shell-main {
                    margin-right: var(--sidebar-w);
                    min-height: 100vh;
                    display: flex; flex-direction: column;
                    transition: margin-right .24s ease;
                }

                /* ---------- topbar ---------- */
                .shell-topbar {
                    position: sticky; top: 0; z-index: 30;
                    height: var(--topbar-h);
                    display: flex; align-items: center; gap: 14px;
                    padding: 0 22px;
                    background: rgba(255,255,255,.88);
                    backdrop-filter: blur(10px);
                    border-bottom: 1.5px solid #e8eaf5;
                }
                .shell-toggle {
                    width: 40px; height: 40px; flex-shrink: 0;
                    display: grid; place-items: center;
                    background: #eef2ff; color: #4338ca;
                    border: 1.5px solid #e0e7ff; border-radius: 12px;
                    font-size: 16px; cursor: pointer; transition: all .18s;
                }
                .shell-toggle:hover {
                    background: #4f46e5; color: #fff; border-color: #4f46e5;
                    box-shadow: 0 6px 16px rgba(79,70,229,.28);
                }
                .shell-toggle--mobile { display: none; }
                .shell-toggle:focus-visible,
                .shell-drawer-close:focus-visible,
                .shell-crumb:focus-visible { outline: 2.5px solid #7c3aed; outline-offset: 2px; }

                /* the heading takes the free space, so the account chip sits at the far end */
                .shell-heading { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
                .shell-title {
                    margin: 0; font-size: 15.5px; font-weight: 800; color: #1e1b4b;
                    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
                }
                .shell-crumbs {
                    display: flex; align-items: center; gap: 7px; flex-wrap: wrap;
                    font-size: 11.5px; color: #9ca3af;
                }
                .shell-crumb {
                    display: inline-flex; align-items: center; gap: 5px;
                    color: #6b7280; font-weight: 700; text-decoration: none; transition: color .15s;
                }
                .shell-crumb:hover { color: #4f46e5; }
                .shell-crumb-sep { font-size: 8px; color: #c7d2fe; }

                .shell-topbar-actions {
                    flex-shrink: 0;
                    display: flex; align-items: center; gap: 10px;
                }

                /* ---------- content ---------- */
                .shell-content {
                    flex: 1;
                    padding: 22px;
                    min-height: calc(100vh - var(--topbar-h) - 70px);
                }

                .shell-footer {
                    display: flex; align-items: center; gap: 14px;
                    padding: 10px 26px 24px;
                }
                .shell-footer-line {
                    flex: 1; height: 1px;
                    background: linear-gradient(90deg, transparent, #dfe3f3, transparent);
                }
                .shell-footer-text { font-size: 11.5px; font-weight: 700; color: #9ca3af; white-space: nowrap; }

                /* ---------- mobile ---------- */
                .shell-backdrop {
                    position: fixed; inset: 0; z-index: 39;
                    background: rgba(30,27,75,.5);
                    backdrop-filter: blur(2px);
                    animation: shell-fade .2s ease;
                }
                @keyframes shell-fade { from { opacity: 0; } to { opacity: 1; } }

                @media (max-width: 1023px) {
                    .shell { --sidebar-w: 288px; }
                    .shell-sidebar { transform: translateX(100%); width: 288px; }
                    .shell-sidebar.is-open { transform: translateX(0); }
                    .shell-main { margin-right: 0; }
                    .shell-toggle--desktop { display: none; }
                    .shell-toggle--mobile { display: grid; }
                    .shell-content { padding: 16px; }
                    .shell-drawer-close {
                        position: absolute; top: 14px; left: 14px; z-index: 2;
                        display: grid; place-items: center;
                        width: 32px; height: 32px;
                        background: rgba(255,255,255,.12); color: #e0e7ff;
                        border: none; border-radius: 10px; cursor: pointer; font-size: 12px;
                    }
                    .shell-drawer-close:hover { background: rgba(255,255,255,.24); }
                }

                @media (max-width: 720px) {
                    .shell-topbar { padding: 0 14px; gap: 10px; }
                    .shell-footer { padding: 10px 16px 20px; }
                }

                @media (prefers-reduced-motion: reduce) {
                    .shell *, .shell *::before, .shell *::after {
                        animation-duration: .01ms !important;
                        transition-duration: .01ms !important;
                    }
                }
            `}</style>
		</div>
	);
};

export default SadAntLayout;